/**
 * @copyright Copyright 2021 Epic Systems Corporation
 * @file state/store for in progress apps widget
 * @module Epic.AppOrchard.State.InProgressApps
 */

import { buildSharedState } from "@epic/react-redux-booster";
import { DownloadApprovedStatus, InstallStatus } from "ao/data";
import store from "ao/store";
import { IInProgressApp } from "ao/types";
import { searchHasMatch } from "ao/utils/helpers";

/// TYPES ///

export interface IState {
	// Map between the org ID and the in progress apps for a customer org
	readonly appsForOrg: Map<number, IInProgressApp[]> | undefined;
	// Whether app data has been loaded for a customer org
	readonly hasLoadedForOrg: Map<number, boolean> | undefined;
}

/**
 * Search params to identify the apps to select from the store
 */
interface IAppsSelectorParams {
	orgId: number;
	searchTerm: string;
}

/// INIT ///

/**
 * Returns an empty initial state
 */
export function getInitialState(): IState {
	return {
		hasLoadedForOrg: undefined,
		appsForOrg: undefined,
	};
}

//#region REDUCERS

/**
 * Make JS Date objects from string dates loaded from the server
 * @param app app to transform
 * @returns app with JS Date objects
 */
function makeJSDates(app: IInProgressApp): IInProgressApp {
	return {
		...app,
		downloadRequest: {
			userName: app.downloadRequest.userName,
			instant: new Date(app.downloadRequest.instant),
		},
		nonProdLicensedOn: app.nonProdLicensedOn && new Date(app.nonProdLicensedOn),
		prodLicensedOn: app.prodLicensedOn && new Date(app.prodLicensedOn),
	};
}

/**
 * Update in progress apps for a customer org and mark in progress apps as loaded from server
 * @param state current state
 * @param params reducer params (org ID and apps to add)
 * @returns updated state
 */
export function updateAppsAfterLoad(
	state: IState,
	params: { orgId: number; apps: IInProgressApp[] },
): IState {
	const { orgId, apps } = params;

	// update state to indicate we've loaded data for this org
	var loadedMap = state.hasLoadedForOrg
		? new Map<number, boolean>([...Array.from(state.hasLoadedForOrg), [orgId, true]])
		: new Map<number, boolean>([[orgId, true]]);

	const appsEntryForOrg = apps.map((a) => makeJSDates(a));

	// update data loaded for the org
	const appsMap = state.appsForOrg
		? new Map<number, IInProgressApp[]>([...Array.from(state.appsForOrg), [orgId, appsEntryForOrg]])
		: new Map<number, IInProgressApp[]>([[orgId, appsEntryForOrg]]);

	return {
		hasLoadedForOrg: loadedMap,
		appsForOrg: appsMap,
	};
}

/**
 * Add or update not implementing now details
 * @param state current state
 * @param params reducer params (customer org ID and apps to add)
 * @returns updated state
 */
export function updateInstallStatus(
	state: IState,
	params: { appId: number; orgId: number; newStatus: InstallStatus },
): IState {
	const { appId, orgId, newStatus } = params;
	const currentApps = getAppsOfType(state, orgId, "", undefined);
	const updatedApps = currentApps.map((app) =>
		app.appId !== appId ? app : { ...app, installStatus: newStatus },
	);

	return {
		hasLoadedForOrg: state.hasLoadedForOrg,
		appsForOrg: new Map<number, IInProgressApp[]>([
			...Array.from(state.appsForOrg!),
			[orgId, updatedApps],
		]),
	};
}

/**
 * Add requested app after customer requests download
 * @param state current state
 * @param params reducer params (org ID and apps to add)
 * @returns updated state
 */
export function addRequestedApp(state: IState, params: { orgId: number; app: IInProgressApp }): IState {
	const { orgId, app } = params;

	if (!getHasLoaded(state, orgId) || !hasDataForOrg(state, orgId)) {
		return state;
	}

	const appsForOrg = getAppsOfType(state, orgId, "", undefined);
	const appToAdd = makeJSDates(app);
	const appsEntryForOrg = [...appsForOrg, appToAdd];

	// update data loaded for the org
	const appsMap = new Map<number, IInProgressApp[]>([
		...Array.from(state.appsForOrg!),
		[orgId, appsEntryForOrg],
	]);

	return {
		hasLoadedForOrg: state.hasLoadedForOrg,
		appsForOrg: appsMap,
	};
}

//#endregion

//#region SELECTORS

/**
 * Check whether data has been loaded for a customer org
 * @param state current state
 * @param orgId: customer org id
 * @returns
 */
export function getHasLoaded(state: IState, orgId: number): boolean {
	return state.hasLoadedForOrg !== undefined && state.hasLoadedForOrg.get(orgId) === true;
}

function hasDataForOrg(state: IState, orgId: number): boolean {
	return state.appsForOrg !== undefined && state.appsForOrg.get(orgId) !== undefined;
}

/**
 * Helper to filter apps by search term
 * @param searchTerm search term to filter apps on
 * @param app app being considered for inclusion in results
 * @returns true if passes filter else false
 */
function filterBySearchTerm(searchTerm: string, app: IInProgressApp): boolean {
	return (
		searchHasMatch(searchTerm, app.appName) ||
		searchHasMatch(searchTerm, app.appOrgName) ||
		searchHasMatch(searchTerm, app.prodClientId) ||
		searchHasMatch(searchTerm, app.nonProdClientId)
	);
}

/**
 * Shared helper that filters to apps of a given type + search term filtering
 *
 * Checks if data has been loaded for the org too
 * @param state current state
 * @param orgId customer org ID
 * @param searchTerm search term to further filter matching apps
 * @param filterFn function to filter to type of app
 * @returns list of apps matching filters
 */
function getAppsOfType(
	state: IState,
	orgId: number,
	searchTerm: string,
	filterFn: ((app: IInProgressApp) => boolean) | undefined,
): IInProgressApp[] {
	if (!getHasLoaded(state, orgId) || !hasDataForOrg(state, orgId)) {
		return [];
	}
	let apps = state.appsForOrg!.get(orgId)!;
	if (filterFn && apps.length > 0) {
		apps = apps.filter(filterFn);
	}

	if (searchTerm && apps.length > 0) {
		apps = apps.filter((a) => filterBySearchTerm(searchTerm, a));
	}

	return apps;
}

/**
 * Get download request apps for a customer that are waiting on vendor approval
 * @param state current state
 * @param params filter params
 */
export function getWaitingOnVendorApps(state: IState, params: IAppsSelectorParams): IInProgressApp[] {
	const { orgId, searchTerm } = params;
	const apps = getAppsOfType(
		state,
		orgId,
		searchTerm,
		(a) => a.downloadApprovedStatus === DownloadApprovedStatus.Unapproved,
	);
	return apps.sort((a, b) => a.appName.localeCompare(b.appName));
}

/**
 * Get download request apps for a customer that have the download request approved by the vendor
 * 	but the install has not started yet
 * @param state current state
 * @param params filter params
 */
export function getNotStartedApps(state: IState, params: IAppsSelectorParams): IInProgressApp[] {
	const { orgId, searchTerm } = params;
	const apps = getAppsOfType(
		state,
		orgId,
		searchTerm,
		(a) =>
			(a.downloadApprovedStatus === DownloadApprovedStatus.Approved ||
				a.downloadApprovedStatus === DownloadApprovedStatus.NonProdOnly) &&
			(a.installStatus === null || a.installStatus === InstallStatus.NotStarted),
	);
	return apps.sort((a, b) => a.appName.localeCompare(b.appName));
}

/**
 * Get download request apps for a customer that are marked as in progress/implementing
 * 	but the install has not started yet
 * @param state current state
 * @param params filter params
 */
export function getImplementingApps(state: IState, params: IAppsSelectorParams): IInProgressApp[] {
	const { orgId, searchTerm } = params;
	const apps = getAppsOfType(
		state,
		orgId,
		searchTerm,
		(a) =>
			(a.downloadApprovedStatus === DownloadApprovedStatus.Approved ||
				a.downloadApprovedStatus === DownloadApprovedStatus.NonProdOnly) &&
			a.installStatus === InstallStatus.InProgress,
	);
	return apps.sort((a, b) => a.appName.localeCompare(b.appName));
}

//#endregion

/// BUILD IT ///

const builtState = buildSharedState({
	init: getInitialState,
	reducers: {
		updateAppsAfterLoad,
		updateInstallStatus,
		addRequestedApp,
	},
	selectors: {
		getHasLoaded,
		getNotStartedApps,
		getWaitingOnVendorApps,
		getImplementingApps,
	},
});
store.addSharedState(builtState.sharedState, "inProgressApps");

export const { actionCreators: inProgressAppsActions, useSharedState: useInProgressAppsState } = builtState;
